import { useState } from "react";

import { Alert, Button, FormField, Modal, TextInput, useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useStoreQuery } from "@/shared/services/useStoreQuery";
import { useDispatchJntShipmentMutation } from "@/modules/orders/services/useShipment";

type Props = {
  storeSlug: string;
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
};

/**
 * J&T dispatch form. J&T's create-order needs a pickup (sender) address and
 * the parcel weight; the delivery address is derived from the order on the
 * backend. Unlike Bosta there is no pickup-location API, so the seller enters
 * their pickup address here.
 */
export function JntDispatchSheet({ storeSlug, orderId, isOpen, onClose }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const { data: store } = useStoreQuery({ storeSlug });
  const { mutateAsync: dispatch, isPending } = useDispatchJntShipmentMutation();

  // Pickup contact defaults to the store's name + WhatsApp number; `null`
  // override means "untouched" so the store value applies without a
  // state-syncing effect. The seller can still edit either field.
  const [senderNameOverride, setSenderNameOverride] = useState<string | null>(
    null,
  );
  const [senderPhoneOverride, setSenderPhoneOverride] = useState<string | null>(
    null,
  );
  const senderName = senderNameOverride ?? store?.storeName ?? "";
  const senderPhone = senderPhoneOverride ?? store?.whatsappNumber ?? "";
  const [senderProvince, setSenderProvince] = useState("");
  const [senderCity, setSenderCity] = useState("");
  const [senderArea, setSenderArea] = useState("");
  const [senderStreet, setSenderStreet] = useState("");
  const [weight, setWeight] = useState("1");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const weightKg = Number(weight);
  const canSubmit =
    !isPending &&
    senderName.trim().length >= 2 &&
    senderPhone.trim().length >= 6 &&
    senderProvince.trim().length >= 2 &&
    senderCity.trim().length >= 2 &&
    senderArea.trim().length >= 2 &&
    senderStreet.trim().length >= 2 &&
    Number.isFinite(weightKg) &&
    weightKg >= 0.01 &&
    weightKg <= 30;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitError(null);
    try {
      await dispatch({
        storeSlug,
        orderId,
        senderName: senderName.trim(),
        senderPhone: senderPhone.trim(),
        senderProvince: senderProvince.trim(),
        senderCity: senderCity.trim(),
        senderArea: senderArea.trim(),
        senderStreet: senderStreet.trim(),
        weightKg,
      });
      toast.success(t(keys.jnt.dispatchedNotice));
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : t(keys.errors.generic),
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      elevated
      title={t(keys.jnt.dispatchTitle)}
      closeLabel={t(keys.common.close)}
      footerActions={
        <Button
          variant="primary"
          size="md"
          disabled={!canSubmit}
          onClick={() => void handleSubmit()}
        >
          {isPending ? t(keys.common.loading) : t(keys.jnt.dispatchAction)}
        </Button>
      }
    >
      <div className="mx-auto flex w-full max-w-[520px] flex-col gap-4">
        <p className="text-sm leading-relaxed text-[color:var(--text-secondary)]">
          {t(keys.jnt.dispatchIntro)}
        </p>

        {submitError ? <Alert message={submitError} /> : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField label={t(keys.jnt.senderNameLabel)} name="senderName">
            <TextInput
              name="senderName"
              value={senderName}
              onChange={(e) => setSenderNameOverride(e.target.value)}
            />
          </FormField>
          <FormField label={t(keys.jnt.senderPhoneLabel)} name="senderPhone">
            <TextInput
              name="senderPhone"
              type="tel"
              dir="ltr"
              value={senderPhone}
              onChange={(e) =>
                setSenderPhoneOverride(e.target.value.replace(/\s+/g, ""))
              }
            />
          </FormField>
          <FormField
            label={t(keys.jnt.senderProvinceLabel)}
            name="senderProvince"
          >
            <TextInput
              name="senderProvince"
              value={senderProvince}
              onChange={(e) => setSenderProvince(e.target.value)}
            />
          </FormField>
          <FormField label={t(keys.jnt.senderCityLabel)} name="senderCity">
            <TextInput
              name="senderCity"
              value={senderCity}
              onChange={(e) => setSenderCity(e.target.value)}
            />
          </FormField>
          <FormField label={t(keys.jnt.senderAreaLabel)} name="senderArea">
            <TextInput
              name="senderArea"
              value={senderArea}
              onChange={(e) => setSenderArea(e.target.value)}
            />
          </FormField>
          <FormField label={t(keys.jnt.senderStreetLabel)} name="senderStreet">
            <TextInput
              name="senderStreet"
              value={senderStreet}
              onChange={(e) => setSenderStreet(e.target.value)}
            />
          </FormField>
        </div>

        <FormField label={t(keys.jnt.weightLabel)} name="weight">
          <TextInput
            name="weight"
            type="number"
            dir="ltr"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
          />
        </FormField>
        <p className="text-[12px] text-[color:var(--text-muted)]">
          {t(keys.jnt.weightHint)}
        </p>
      </div>
    </Modal>
  );
}
